/**
 * Client-side encryption for sensitive user data (journals).
 * Uses Web Crypto API with AES-GCM for authenticated encryption.
 * 
 * Key derivation: PBKDF2 from user password + user ID as salt.
 * This means only the user can decrypt their own journals.
 */

const PBKDF2_ITERATIONS = 100000;
const KEY_LENGTH = 256;

/**
 * Derive an encryption key from the user's password and ID.
 * @param {string} password - User's password
 * @param {string} userId - User's unique ID (used as salt)
 * @returns {Promise<CryptoKey>}
 */
async function deriveKey(password, userId) {
  const encoder = new TextEncoder();
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(userId),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    passwordKey,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt plaintext using AES-GCM.
 * @param {string} plaintext - Text to encrypt
 * @param {CryptoKey} key - Encryption key
 * @returns {Promise<string>} Base64-encoded ciphertext with IV prepended
 */
async function encrypt(plaintext, key) {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
  
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  );

  // Prepend IV to ciphertext: [IV (12 bytes)][ciphertext]
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt ciphertext using AES-GCM.
 * @param {string} ciphertextB64 - Base64-encoded ciphertext with IV prepended
 * @param {CryptoKey} key - Decryption key
 * @returns {Promise<string>} Decrypted plaintext
 */
async function decrypt(ciphertextB64, key) {
  const combined = Uint8Array.from(atob(ciphertextB64), c => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  const decoder = new TextDecoder();
  return decoder.decode(plaintext);
}

/**
 * Encrypt journal content before sending to server.
 * @param {string} content - Journal content
 * @param {string} password - User's password
 * @param {string} userId - User's ID
 * @returns {Promise<string>} Encrypted content
 */
export async function encryptJournal(content, password, userId) {
  const key = await deriveKey(password, userId);
  return encrypt(content, key);
}

/**
 * Decrypt journal content after receiving from server.
 * @param {string} encryptedContent - Encrypted journal content
 * @param {string} password - User's password
 * @param {string} userId - User's ID
 * @returns {Promise<string>} Decrypted content
 */
export async function decryptJournal(encryptedContent, password, userId) {
  // Check if content is actually encrypted (Base64 with IV prepended)
  // Plaintext journals won't be valid Base64 or will fail decryption
  try {
    const key = await deriveKey(password, userId);
    return await decrypt(encryptedContent, key);
  } catch (error) {
    // If decryption fails, assume it's plaintext (legacy journal)
    return encryptedContent;
  }
}

/**
 * Batch decrypt multiple journals.
 * @param {Array} journals - Array of journal objects with encrypted content
 * @param {string} password - User's password
 * @param {string} userId - User's ID
 * @returns {Promise<Array>} Journals with decrypted content
 */
export async function decryptJournals(journals, password, userId) {
  return Promise.all(
    journals.map(async (journal) => {
      try {
        const key = await deriveKey(password, userId);
        const decrypted = await decrypt(journal.content, key);
        return { ...journal, content: decrypted };
      } catch (error) {
        // If decryption fails, assume plaintext (legacy journal)
        return journal;
      }
    })
  );
}
